import { List, ListItem, ListItemText, ListSubheader } from "@material-ui/core";
import React from "react";

import { useAppUser } from "components/AppUserProvider";
import { useOrg } from "components/OrgProvider";
import { useOrgUser } from "components/OrgUserProvider";
import { Nullable } from "models/Nullable";
import { OfferRoom } from "models/OfferRoom";
import { OrgRoles } from "models/OrgRoles";
import { HourRateInput } from "screens/MyOrgScreen";

import { formatMoney, getSubTotal } from "./SummaryTable";

type Props = {
    offerRooms: OfferRoom[]
    manualPrice: Nullable<number>
    discount: Nullable<number>
}

export function getOfferTotalTime(offerRooms: OfferRoom[]) {
    return offerRooms
        .map(or => Object.values(or.priceFields)
            .map(rpf => (rpf.priceField.expectedTime?.minutes ?? 0) * rpf.count))
        .flat()
        .reduce((acc, val) => acc + val, 0)
}
export function getTotalTimeString(totalTime: number) {
    const totalHours = Math.floor(totalTime / 60)
    const totalMinutes = totalTime % 60
    const totalHourString = totalHours ? [(totalHours + ' time' + (totalHours > 1 ? 'r' : ''))] : []
    const totalMinutesString = totalMinutes ? [(totalMinutes + ' minutter')] : []
    return [totalHourString, totalMinutesString].flat().join(' og ')

}
export function getOfferTotalMaterialCost(offerRooms: OfferRoom[]) {
    return offerRooms
        .map(or => Object.values(or.priceFields)
            .map(rpf => (rpf.priceField.materialPrice ?? 0) * rpf.count))
        .flat()
        .reduce((acc, val) => acc + val, 0)
}

export function InternalOfferAccounting({ offerRooms, discount, manualPrice }: Props) {
    const [orgDoc] = useOrg()
    const [appUserDoc] = useAppUser()
    const [orgUserDoc] = useOrgUser()

    const org = orgDoc?.data || null
    const appUser = appUserDoc?.data || null
    const orgUser = orgUserDoc?.data || null

    const isOwner = orgUser?.orgRoles.map[OrgRoles.FlatEnum.OWNER]

    const totalTime = getOfferTotalTime(offerRooms)
    const totalMaterialCost = getOfferTotalMaterialCost(offerRooms)
    const totalWage = (totalTime / 60) * (org?.hourRate?.default ?? 0)
    const totalCost = totalMaterialCost + totalWage
    const subTotal = getSubTotal(offerRooms)
    const totalPrice = manualPrice ?? subTotal - (discount || 0);
    const estimatedIncome = totalPrice - totalCost

    const content = [
        (org && orgUser && appUser && totalTime && isOwner) ? (
            <HourRateInput key="hourRate" orgUser={orgUser} org={org} appUser={appUser} />
        ) : null,
        totalMaterialCost ? (
            <ListItem dense key="materialCost">
                <ListItemText
                    primary="Materialkostnad"
                    secondary={formatMoney(totalMaterialCost)}
                />
            </ListItem>
        ) : null,
        (totalWage && isOwner) ? (
            <ListItem dense key="wage">
                {org?.hourRate?.default ? (
                    <ListItemText
                        primary="Lønnsutgifter for Estimert tid"
                        secondary={formatMoney(totalWage)}
                    />
                ) : null}
            </ListItem>
        ) : null,
        manualPrice ? (
            <ListItem dense key="manualPrice">
                <ListItemText
                    primary="Manuall Pris"
                    secondary={formatMoney(manualPrice)}
                />
            </ListItem>
        ) : null,
        discount ? (
            <ListItem dense key="discount">
                <ListItemText
                    primary="Rabatt"
                    secondary={formatMoney(discount)}
                />
            </ListItem>
        ) : null,
        <ListItem dense key="subTotal">
            <ListItemText
                primary="Delsum (Sum av prisfeltenes listepriser)"
                secondary={formatMoney(subTotal)}
            />
        </ListItem>,
        totalTime ? (
            <ListItem dense key="estimatedTime">
                <ListItemText
                    primary="Estimert tid"
                    secondary={getTotalTimeString(totalTime)}
                />
            </ListItem>
        ) : null,
        isOwner ? <ListSubheader key="costEstimateSubheader">Kostnadsoverslag</ListSubheader> : null,
        isOwner ? (
            <ListItem dense key="totalCost">
                <ListItemText
                    primary="Total kostnad"
                    secondary={formatMoney(totalCost)}
                />
            </ListItem>
        ) : null,
        isOwner ? (
            <ListItem dense key="estimatedIncome">
                <ListItemText
                    primary="Estimert inntekt"
                    color="primary"
                    secondary={formatMoney(estimatedIncome)}
                />
            </ListItem>
        ) : null,
    ]

    return (
        <List>
            {content.some(c => c !== null) ? content : (
                <ListItem>
                    <ListItemText
                        primary="Ingen informasjon"
                        secondary="Ingen informasjon å vise. Om du ønsker å bruke denne siden må du legge til estimert tid og/eller materialkostnad på prisfeltene som brukes i anbudet"
                    />
                </ListItem>
            )}
        </List>
    )
}